import React, { useState, useEffect } from 'react';
import { X, Tag, Check, AlertCircle } from 'lucide-react';
import billingService from '../../services/billingService';
import './Billing.css';

const CheckoutModal = ({
    isOpen,
    onClose,
    plan,
    billingCycle,
    onFinalize,
    isProcessing: externalProcessing
}) => {
    const [couponCode, setCouponCode] = useState('');
    const [coupon, setCoupon] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState(null);
    const [internalProcessing, setInternalProcessing] = useState(false);

    const isProcessing = externalProcessing || internalProcessing;

    useEffect(() => {
        if (!isOpen) {
            setCouponCode('');
            setCoupon(null);
            setError(null);
        }
    }, [isOpen]);

    const handleApplyCoupon = async () => {
        if (!couponCode) return;

        setIsValidating(true);
        setError(null);
        try {
            const result = await billingService.validateCoupon(couponCode, plan.id);
            setCoupon(result);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid coupon code');
            setCoupon(null);
        } finally {
            setIsValidating(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCoupon(null);
        setCouponCode('');
        setError(null);
    };

    const originalPrice = billingCycle === 'annual' ? (plan?.annualPrice || 0) : (plan?.price || 0);
    let finalPrice = originalPrice;
    let savings = 0;

    if (coupon) {
        if (coupon.discountType === 'percentage') {
            savings = originalPrice * (coupon.discountValue / 100);
        } else {
            savings = coupon.discountValue;
        }
        finalPrice = Math.max(0, originalPrice - savings);
    }

    const handleConfirm = async () => {
        setInternalProcessing(true);
        try {
            await onFinalize(plan.id, billingCycle, coupon?.code);
        } catch (err) {
            setError(err.message || 'Checkout failed');
        } finally {
            setInternalProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="dialog-overlay" onClick={onClose}>
            <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                <div className="dialog-header">
                    <h3 className="dialog-title">Confirm Subscription</h3>
                    <button className="dialog-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="dialog-body">
                    <p className="dialog-message" style={{ marginBottom: '20px' }}>
                        Review your selection before proceeding to payment.
                    </p>

                    <div className="checkout-summary">
                        <div className="summary-item">
                            <span className="label">Plan</span>
                            <span className="value">{plan.name}</span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Billing Cycle</span>
                            <span className="value" style={{ textTransform: 'capitalize' }}>{billingCycle}</span>
                        </div>

                        <div className="summary-item" style={{ marginTop: '12px' }}>
                            <span className="label">Subtotal</span>
                            <span className="value">${(originalPrice || 0).toFixed(2)}</span>
                        </div>

                        {coupon && (
                            <div className="summary-item" style={{ color: 'var(--success)' }}>
                                <span className="label">Discount ({coupon.code})</span>
                                <span className="value">-${(savings || 0).toFixed(2)}</span>
                            </div>
                        )}

                        <div className="summary-item total">
                            <span className="label">Total Today</span>
                            <span className="value">
                                {coupon && <span className="original-price">${(originalPrice || 0).toFixed(2)}</span>}
                                ${(finalPrice || 0).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <div className="coupon-section">
                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px', color: 'var(--text-secondary)' }}>
                            Have a Promo Code?
                        </label>
                        {!coupon ? (
                            <div className="coupon-input-group">
                                <input
                                    type="text"
                                    className="coupon-input"
                                    placeholder="Enter code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                    disabled={isValidating}
                                />
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode || isValidating}
                                >
                                    {isValidating ? '...' : 'Apply'}
                                </button>
                            </div>
                        ) : (
                            <div className="discount-tag">
                                <Tag size={14} />
                                <span>{coupon.code} Applied</span>
                                <button onClick={handleRemoveCoupon} style={{ marginLeft: '4px' }}>
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                        {error && (
                            <div className="coupon-status error">
                                <AlertCircle size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'text-bottom' }} />
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                <div className="dialog-footer">
                    <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={isProcessing}>
                        Cancel
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : 'Pay & Subscribe'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CheckoutModal;
